import React, { useState, useRef, useEffect } from "react";

import { MinusIcon, PlusIcon } from "shared/ui/icons";
import { formatDurationLabel } from "shared/utils";
import styles from "./TimePickerPopup.module.css";

type TimeField = "hours" | "minutes" | "seconds";

const pad = (n: number) => n.toString().padStart(2, "0");

interface Props {
  hours: number;
  minutes: number;
  seconds: number;
  duration: number;
  presets: number[];
  onAdjust: (field: TimeField, delta: number) => void;
  onSelectPreset: (seconds: number) => void;
}

export const TimePickerPopup: React.FC<Props> = ({
  hours, minutes, seconds, duration, presets, onAdjust, onSelectPreset,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fields: [TimeField, number][] = [
    ["hours", hours],
    ["minutes", minutes],
    ["seconds", seconds],
  ];

  return (
    <div className={styles.wrapper} ref={ref}>
      <button className={styles.display} onClick={() => setOpen((v) => !v)}>
        {pad(hours)}:{pad(minutes)}:{pad(seconds)}
      </button>

      {open && (
        <div className={styles.popup}>
          <div className={styles.columns}>
            {fields.map(([field, value], i) => (
              <React.Fragment key={field}>
                {i > 0 && <span className={styles.sep}>:</span>}
                <div className={styles.column}>
                  <button className={styles.columnBtn} onClick={() => onAdjust(field, 1)}>
                    <PlusIcon />
                  </button>
                  <span className={styles.value}>{pad(value)}</span>
                  <button className={styles.columnBtn} onClick={() => onAdjust(field, -1)}>
                    <MinusIcon />
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>

          {presets.length > 0 && (
            <div className={styles.presets}>
              {presets.map((p) => (
                <button
                  key={p}
                  className={`${styles.preset} ${duration === p ? styles.active : ""}`}
                  onClick={() => onSelectPreset(p)}
                >
                  {formatDurationLabel(p)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
